/**
 * Who a client may authorise their case to be discussed with.
 *
 * Mirrors `backend/src/config/consentDocuments.ts`. The frontend cannot import from the backend, so
 * this is the same arrangement `referralSources.ts` uses: one file on each side, and a test that
 * fails when they drift (`npm run test-consent-documents`).
 *
 * It lives here rather than inside `ConsentGate.tsx` because a list declared inside the one component
 * that renders it is how the referral-source dropdown ended up three copies deep, with two of them
 * missing entries that had been in the canonical list for months.
 *
 * ## Lloyd's three, 14 Oct 2026
 *
 * He cut his five of 16 Sep to three: the referral company (including CDL Consultants), someone
 * paying the fees, and a named family member. `referring-organisation` is the value the CDL reply
 * has always checked, so it is back in use; `payor` keeps its key with a new meaning. The rest are
 * retired: stored answers may still carry them, but no screen offers them.
 */

export type ConsentRecipient = 'referring-organisation' | 'payor' | 'family';

/**
 * What a stored answer may hold, which is wider than what a client can now choose.
 *
 * Rows written under earlier wording still read, and anything typing an API response has to allow
 * for them. They are deliberately absent from `RECIPIENT_ORDER`, so no screen can offer one.
 */
export type StoredConsentRecipient =
  | ConsentRecipient
  | 'cdl-csa-reporting'
  | 'employer'
  | 'carrier'
  | 'other'
  | 'carrier-employer';

/** His labels as confirmed on 14 Oct. These are what the client reads. */
export const RECIPIENT_LABELS: Record<ConsentRecipient, string> = {
  'referring-organisation': 'The company that referred me to the firm (for example, CDL Consultants, TVC, or another law firm)',
  payor: 'Someone paying my legal fees on my behalf, such as a family member or my employer',
  family: 'A family member',
};

/** His order. */
export const RECIPIENT_ORDER: ConsentRecipient[] = ['referring-organisation', 'payor', 'family'];

/** The standing note above the boxes. */
export const SHARING_INTRO =
  'Some clients are referred to us by a company such as CDL Consultants, have their fees paid by ' +
  'someone else, or have family who ask how their case is going. The Firm will only share a short ' +
  'case status with the people you approve below, and only for the matter this consent applies to.';

/** His sentence about changing an answer, which the decline path also has to honour. */
export const SHARING_CHANGE_NOTE =
  'You may change your answer at any time. Changes take effect immediately and apply going forward; ' +
  'they do not affect updates already sent under a prior consent.';

/** His heading for the block. */
export const SHARING_PROMPT =
  'I authorize the Firm to share case status updates for this matter (check all that apply):';
